import { Component, Input } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';

@Component({
  selector: 'bhub-pdf-field-cell',
  standalone: false,
  templateUrl: './pdf-field-cell.component.html',
  styleUrls: ['./pdf-field-cell.component.scss']
})
export class PdfFieldCellComponent extends BaseCellComponent {
  @Input() viewManager: ViewManager | null = null;

  label: string = '';
  showLabel: boolean = true;
  fontSize: number = 12;
  displayValue: string = '';
  hasOverride: boolean = false;

  protected initializeCell(): void {
    this.updateDisplayProperties();
  }

  protected onCellOrDataChange(): void {
    this.updateDisplayProperties();
  }

  protected onCellUpdate(): void {
    this.updateDisplayProperties();
  }

  protected onDataSet(data: any): void {
    this.updateDisplayProperties();
  }

  reloadData(): void {
    this.updateDisplayProperties();
  }

  private updateDisplayProperties(): void {
    if (!this.cell) return;

    this.label = this.getConfigValue('pdfFieldLabel', '');
    this.showLabel = this.getConfigValue('pdfShowLabel', true);
    this.fontSize = this.getConfigValue('pdfFontSize', 12);

    // If there's a manual override from PDF edit mode, use it
    const overrideValue = this.getConfigValue('pdfOverrideValue', undefined);
    if (overrideValue !== undefined) {
      this.hasOverride = true;
      this.displayValue = overrideValue;
      return;
    }
    this.hasOverride = false;

    // Get dynamic value from context
    const contextVariable = this.getDataBindingValue('contextVariable');
    const fieldName = this.getDataBindingValue('fieldName');
    const format = this.getConfigValue('pdfFieldFormat', '');

    if (contextVariable && fieldName && this.viewManager?.contextData) {
      const dataObject = this.viewManager.contextData[contextVariable];
      if (dataObject) {
        let value: any;
        if (Array.isArray(dataObject) && dataObject.length > 0) {
          value = dataObject[0][fieldName];
        } else if (typeof dataObject === 'object') {
          value = dataObject[fieldName];
        }

        this.displayValue = this.formatValue(value, format);
      } else {
        this.displayValue = `[${contextVariable} not loaded]`;
      }
    } else {
      this.displayValue = '[Configure data binding]';
    }
  }

  private formatValue(value: any, format: string): string {
    if (value === null || value === undefined) {
      return '[No value]';
    }

    switch (format) {
      case 'currency': {
        const num = this.toNumber(value);
        return num === null
          ? String(value)
          : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
      }
      case 'number': {
        const num = this.toNumber(value);
        return num === null ? String(value) : new Intl.NumberFormat('en-US').format(num);
      }
      case 'percent': {
        const num = this.toNumber(value);
        return num === null
          ? String(value)
          : new Intl.NumberFormat('en-US', {
              style: 'percent',
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            }).format(num);
      }
      case 'date': {
        const date = new Date(value);
        return isNaN(date.getTime()) ? String(value) : date.toLocaleDateString('en-US');
      }
      default:
        // 'raw', '' (legacy), or unknown — no formatting
        return String(value);
    }
  }

  /**
   * Coerce a value to a number, stripping currency symbols and thousands separators
   * so string values from datasets (e.g. "$1,234.50") still format correctly.
   */
  private toNumber(value: any): number | null {
    if (typeof value === 'number') {
      return isNaN(value) ? null : value;
    }
    if (typeof value === 'string') {
      const cleaned = value.replace(/[$€£¥,\s]/g, '');
      if (cleaned === '') return null;
      const num = Number(cleaned);
      return isNaN(num) ? null : num;
    }
    return null;
  }
}
